// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Ask about this chat" on phones, as pure functions. The composer's sparkle opens the
// Konvo panel about the open chat, Ask Konvo on a long-pressed message about that one
// message; the sheet (AskAboutSheet) shows it as a removable context row with starters that
// fit it (a client chat gets "What did we agree?", a group "Catch me up"). Requests carry the
// chat's conversation id (and the message) in their context line, and ask for a drafted reply
// alone in one quote block, so the sheet can offer "Put in reply" for it. Ask Konvo on a
// long-pressed mail opens it about that email ("Put in reply" opens a reply to it). Unit tested
// in askAbout.test.ts.

import type {ChatFocus, QuickTask} from '@app/features/konvo_panel/state/quickTasks';
import {firstName, type StarterContext} from '@app/features/konvo_panel/state/starters';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

/** What the sheet is about: a chat, one message in it, or one email. */
export interface PanelAbout {
	/** The chat; an email reads as a one-to-one chat with its sender. */
	chat: Omit<ChatFocus, 'source'> & {source: ChatFocus['source'] | 'mail'};
	/** Where "Put in reply" writes: the Konvo (or Teams) composer by channel id, a WhatsApp chat, or a reply to an email. */
	target: {kind: 'konvo'; channelId: string} | {kind: 'whatsapp'; jid: string} | {kind: 'mail'; messageId: string};
	/** The picture on the context row: a Konvo user, or a WhatsApp chat or person. */
	avatar: {userId?: string; whatsappJid?: string; whatsappPicture?: boolean};
	/** Asked from one message (long-press), not the whole chat. */
	message?: {id: string; author: string; text: string};
	/** Asked from one email (long press on a mail): the sender is `chat.name`. */
	mail?: {id: string; conversationId: string | null; subject: string; fromAddress: string};
}

export type AskStarterIcon = 'reply' | 'agree' | 'catch_up' | 'summary' | 'task' | 'remind' | 'explain';

export interface AskStarter extends QuickTask {
	starterIcon: AskStarterIcon;
	/** The answer is a reply to put in the composer. */
	draft: boolean;
}

/** Message text kept in the context line (and so in the Assistant DM). */
export const MESSAGE_TEXT_MAX = 300;

const DRAFT_REPLY = msg({message: 'Draft a reply', comment: 'Starter in the "Ask about this chat" sheet.'});
const DRAFT_REPLY_REQ = msg({
	message: 'Draft a reply to the latest message in this chat.',
	comment: 'Request the "Draft a reply" starter sends to the assistant from the "Ask about this chat" sheet.',
});
const AGREED = msg({message: 'What did we agree?', comment: 'Starter in the "Ask about this chat" sheet.'});
const AGREED_REQ = msg({
	message: 'What did we agree in this chat? Decisions, promises and questions still open.',
	comment: 'Request the "What did we agree?" starter sends from the "Ask about this chat" sheet.',
});
const CATCH_UP = msg({
	message: 'Catch me up',
	comment: 'Starter in the "Ask about this chat" sheet for a group chat.',
});
const CATCH_UP_REQ = msg({
	message: 'Catch me up on this chat: what happened since I last read it and what needs me.',
	comment: 'Request the "Catch me up" starter sends from the "Ask about this chat" sheet.',
});
const SUMMARISE = msg({message: 'Summarise', comment: 'Starter in the "Ask about this chat" sheet.'});
const SUMMARISE_REQ = msg({
	message: 'Summarise this chat: what was discussed, what was decided and what is still open.',
	comment: 'Request the "Summarise" starter sends from the "Ask about this chat" sheet.',
});
const TASK_FOR = msg({
	message: 'Task for {name}',
	comment:
		'Starter in the "Ask about this chat" sheet: a ClickUp task for the person in this chat. {name} is a first name.',
});
const TASK_FOR_REQ = msg({
	message: 'Draft a ClickUp task for {name} from what we discussed in this chat, and show me the task to review.',
	comment: 'Request the "Task for <name>" starter sends. {name} is the person in the chat.',
});
const MAKE_TASK = msg({message: 'Make a task', comment: 'Starter in the "Ask about this chat" sheet.'});
const MAKE_TASK_CHAT_REQ = msg({
	message: 'Draft a ClickUp task from what was asked in this chat, and show me the task to review.',
	comment: 'Request the "Make a task" starter sends about a chat.',
});
const MAKE_TASK_MESSAGE_REQ = msg({
	message: 'Draft a ClickUp task from this message, and show me the task to review.',
	comment: 'Request the "Make a task" starter sends about one message.',
});
const REMIND = msg({message: 'Remind me', comment: 'Starter in the "Ask about this chat" sheet.'});
const REMIND_CHAT_REQ = msg({
	message: 'Remind me to get back to this chat. Pick a sensible time from what was said, or tomorrow at 9:00.',
	comment: 'Request the "Remind me" starter sends about a chat.',
});
const REMIND_MESSAGE_REQ = msg({
	message: 'Remind me about this message. Pick a sensible time from what it says, or tomorrow at 9:00.',
	comment: 'Request the "Remind me" starter sends about one message.',
});
const EXPLAIN = msg({
	message: 'Explain',
	comment: 'Starter in the "Ask about this chat" sheet opened from one message.',
});
const EXPLAIN_REQ = msg({
	message: 'Explain this message: what does {name} mean, and what do they need from me?',
	comment: 'Request the "Explain" starter sends about one message. {name} is who wrote it.',
});
const REPLY_TO_THIS = msg({
	message: 'Reply to this',
	comment: 'Starter in the "Ask about this chat" sheet opened from one message.',
});
const REPLY_TO_THIS_REQ = msg({
	message: 'Draft a reply to this message.',
	comment: 'Request the "Reply to this" starter sends about one message.',
});

const MAIL_REPLY_REQ = msg({
	message: 'Draft a reply to this email.',
	comment: 'Request the "Draft a reply" starter sends from the Ask sheet opened on one email.',
});
const MAIL_SUMMARISE_REQ = msg({
	message: 'Summarise this email: what it says, what it asks of me and any dates in it.',
	comment: 'Request the "Summarise" starter sends from the Ask sheet opened on one email.',
});
const MAIL_TASK_REQ = msg({
	message: 'Draft a ClickUp task from this email, and show me the task to review.',
	comment: 'Request the "Make a task" starter sends about one email.',
});
const MAIL_REMIND_REQ = msg({
	message: 'Remind me about this email. Pick a sensible time from what it says, or tomorrow at 9:00.',
	comment: 'Request the "Remind me" starter sends about one email.',
});

function clip(text: string, max: number): string {
	const clean = text.replace(/\s+/g, ' ').trim();
	return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

/** The context line keys for what the sheet is about. */
export function aboutContext(about: PanelAbout): StarterContext {
	if (about.mail) {
		const context: StarterContext = {
			mail_id: about.mail.id,
			subject: clip(about.mail.subject, 200),
			from_name: about.chat.name,
			from_address: about.mail.fromAddress,
			reply_draft: 'quote',
		};
		if (about.mail.conversationId) context.conversation_id = about.mail.conversationId;
		return context;
	}
	const context: StarterContext = {
		conversation: about.chat.conversationId,
		chat_name: about.chat.name,
		reply_draft: 'quote',
	};
	if (about.message) {
		context.message_id = about.message.id;
		context.message_author = about.message.author;
		if (about.message.text.trim()) context.message_text = clip(about.message.text, MESSAGE_TEXT_MAX);
	}
	return context;
}

/** The starters for a chat or a message, in the order the sheet shows them. */
export function askStarters(i18n: I18n, about: PanelAbout, options: {clickUp: boolean}): Array<AskStarter> {
	const context = aboutContext(about);
	const make = (
		id: string,
		starterIcon: AskStarterIcon,
		title: string,
		request: string,
		draft = false,
	): AskStarter => ({
		id,
		group: 'here',
		icon: 'konvo',
		starterIcon,
		title,
		request,
		context,
		draft,
	});
	if (about.mail) {
		const out = [
			make('ask_reply', 'reply', i18n._(DRAFT_REPLY), i18n._(MAIL_REPLY_REQ), true),
			make('ask_summary', 'summary', i18n._(SUMMARISE), i18n._(MAIL_SUMMARISE_REQ)),
		];
		if (options.clickUp) out.push(make('ask_task', 'task', i18n._(MAKE_TASK), i18n._(MAIL_TASK_REQ)));
		out.push(make('ask_remind', 'remind', i18n._(REMIND), i18n._(MAIL_REMIND_REQ)));
		return out;
	}
	if (about.message) {
		const who = firstName(about.message.author) || about.message.author;
		const out = [
			make('ask_explain', 'explain', i18n._(EXPLAIN), i18n._(EXPLAIN_REQ, {name: who})),
			make('ask_reply', 'reply', i18n._(REPLY_TO_THIS), i18n._(REPLY_TO_THIS_REQ), true),
		];
		if (options.clickUp) out.push(make('ask_task', 'task', i18n._(MAKE_TASK), i18n._(MAKE_TASK_MESSAGE_REQ)));
		out.push(make('ask_remind', 'remind', i18n._(REMIND), i18n._(REMIND_MESSAGE_REQ)));
		return out;
	}
	const chat = about.chat;
	const out = [make('ask_reply', 'reply', i18n._(DRAFT_REPLY), i18n._(DRAFT_REPLY_REQ), true)];
	out.push(
		chat.group
			? make('ask_catch_up', 'catch_up', i18n._(CATCH_UP), i18n._(CATCH_UP_REQ))
			: make('ask_agreed', 'agree', i18n._(AGREED), i18n._(AGREED_REQ)),
	);
	out.push(make('ask_summary', 'summary', i18n._(SUMMARISE), i18n._(SUMMARISE_REQ)));
	if (options.clickUp) {
		const name = chat.group ? '' : firstName(chat.name);
		out.push(
			name
				? make('ask_task', 'task', i18n._(TASK_FOR, {name}), i18n._(TASK_FOR_REQ, {name: chat.name}))
				: make('ask_task', 'task', i18n._(MAKE_TASK), i18n._(MAKE_TASK_CHAT_REQ)),
		);
	}
	out.push(make('ask_remind', 'remind', i18n._(REMIND), i18n._(REMIND_CHAT_REQ)));
	return out;
}

export interface ReplyDraft {
	/** The answer without the quote block. */
	before: string;
	after: string;
	/** The reply text, ready for the composer. */
	draft: string;
}

const QUOTE_LINE = /^\s{0,3}>\s?(.*)$/u;
const WRAPPING_QUOTES = /^["“”„«»']+|["“”„«»']+$/gu;

function sameText(a: string, b: string): boolean {
	const norm = (value: string) => value.replace(/\s+/g, ' ').replace(WRAPPING_QUOTES, '').trim().toLowerCase();
	return norm(a) === norm(b);
}

/**
 * The drafted reply in an answer: the last quote block (the context line asks for it there).
 * A quote of the message the sheet is about is the assistant citing it, not a draft.
 */
export function splitReplyDraft(text: string, quotedMessage?: string): ReplyDraft | null {
	const lines = text.split('\n');
	let end = -1;
	for (let index = lines.length - 1; index >= 0; index -= 1) {
		if (QUOTE_LINE.test(lines[index]!)) {
			end = index;
			break;
		}
	}
	if (end === -1) return null;
	let start = end;
	while (start > 0 && QUOTE_LINE.test(lines[start - 1]!)) start -= 1;
	const draft = lines
		.slice(start, end + 1)
		.map((line) => QUOTE_LINE.exec(line)?.[1] ?? '')
		.join('\n')
		.trim()
		.replace(WRAPPING_QUOTES, '')
		.trim();
	if (!draft) return null;
	if (quotedMessage && sameText(draft, quotedMessage)) return null;
	return {
		before: lines.slice(0, start).join('\n').trim(),
		after: lines
			.slice(end + 1)
			.join('\n')
			.trim(),
		draft,
	};
}

const ENGLISH_WORDS = new Set([
	'the',
	'and',
	'to',
	'you',
	'is',
	'it',
	'i',
	'we',
	'of',
	'for',
	'that',
	'on',
	'with',
	'can',
	'will',
	'be',
	'are',
	'this',
	'just',
	'please',
	'thanks',
	'have',
	'your',
	'my',
]);

/** Rough check for the "In English" action: few common English words means another language. */
export function looksEnglish(text: string): boolean {
	const words = text.toLowerCase().match(/[a-z']+/g) ?? [];
	if (words.length < 4) return true;
	const english = words.filter((word) => ENGLISH_WORDS.has(word)).length;
	return english / words.length >= 0.12;
}
